// frontend/modules/drafts/views/DraftsManagementView.tsx
import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Input, Select, Space, Divider, Modal } from 'antd';
import { ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/components/common/ThemeProvider';
import { useDrafts } from '../hooks/useDrafts';
import { DraftProcess, DraftProcessStatus } from '../types';
import DraftProcessList from '../components/DraftProcessList';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

const DraftsManagementView: React.FC = () => {
  const { dark } = useTheme();
  const navigate = useNavigate();
  const [selectedDraft, setSelectedDraft] = useState<DraftProcess | null>(null);
  
  const {
    filteredDraftProcesses,
    isLoading,
    error,
    searchTerm,
    selectedStatus,
    setSearchTerm,
    setSelectedStatus,
    loadDraftProcesses,
    cancelDraftProcess,
    deleteDraftProcess
  } = useDrafts();

  useEffect(() => {
    loadDraftProcesses();
  }, [loadDraftProcesses]);

  // Debug log to monitor filtered draft processes
  useEffect(() => {
    console.log('Filtered draft processes:', filteredDraftProcesses);
  }, [filteredDraftProcesses]);

  const handleViewDraft = (draft: DraftProcess) => {
    setSelectedDraft(draft);
  };

  const handleResumeDraft = (draft: DraftProcess) => {
    try {
      // Parse process data to get linkAddress if available
      let linkAddress = '/drafts/resume'; // Default fallback path
      
      if (draft.processData) {
        try {
          const processData = JSON.parse(draft.processData);
          if (processData.linkAddress) {
            // Use the link address from the draft data
            linkAddress = processData.linkAddress;
          } else if (draft.processType === 'LOAN_APPLICATION') {
            // For loan applications without explicit link, use loans route
            linkAddress = '/loans/application?resume=';
          }
        } catch (error) {
          console.error('Error parsing draft process data:', error);
        }
      }
      
      // Navigate to the appropriate route with the draft ID
      if (linkAddress.includes('?')) {
        // If the link already has query parameters, append the draft ID
        navigate(`${linkAddress}${draft.id}`);
      } else {
        // Otherwise, add the draft ID as a query parameter
        navigate(`${linkAddress}?resume=${draft.id}`);
      }
    } catch (error) {
      console.error('Error resuming draft:', error);
    }
  };

  const handleCancelDraft = async (id: string) => {
    try {
      await cancelDraftProcess(id);
    } catch (error) {
      console.error('Error canceling draft:', error);
    }
  };

  const handleDeleteDraft = async (id: string) => {
    try {
      await deleteDraftProcess(id);
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  return (
    <div className="space-y-6">
      <Card
        className={dark ? 'bg-gray-800 text-white' : ''}
        title={
          <div className="flex justify-between items-center">
            <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
              Draft Processes
            </Title>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadDraftProcesses()}
                loading={isLoading}
              >
                Refresh
              </Button>
            </Space>
          </div>
        }
      >
        <div className="flex flex-wrap gap-4 mb-6">
          <Search
            placeholder="Search by name or process type"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />

          <Select
            placeholder="Filter by status"
            value={selectedStatus}
            onChange={setSelectedStatus}
            style={{ width: 200 }}
            allowClear
          >
            <Option value={DraftProcessStatus.PENDING}>Pending</Option>
            <Option value={DraftProcessStatus.COMPLETED}>Completed</Option>
            <Option value={DraftProcessStatus.CANCELED}>Canceled</Option>
            <Option value={DraftProcessStatus.FAILED}>Failed</Option>
          </Select>
        </div>

        <Divider className={dark ? 'border-gray-700' : ''} />

        {error && (
          <div className="text-red-500 mb-4">
            Error loading drafts: {error}
          </div>
        )}

        <DraftProcessList
          draftProcesses={filteredDraftProcesses}
          onView={handleViewDraft}
          onResume={handleResumeDraft}
          onCancel={handleCancelDraft}
          onDelete={handleDeleteDraft}
        />
      </Card>

      {selectedDraft && (
        <Modal
          title={`Draft Process: ${selectedDraft.name}`}
          open={!!selectedDraft}
          onCancel={() => setSelectedDraft(null)}
          footer={null}
          width={700}
        >
          <div className="space-y-4">
            <div>
              <strong>Process Type:</strong> {selectedDraft.processType}
            </div>
            <div>
              <strong>Status:</strong> {selectedDraft.status}
            </div>
            <div>
              <strong>Created:</strong> {new Date(selectedDraft.createdAt).toLocaleString()}
            </div>
            <div>
              <strong>Last Updated:</strong> {new Date(selectedDraft.updatedAt).toLocaleString()}
            </div>
            {selectedDraft.completedAt && (
              <div>
                <strong>Completed:</strong> {new Date(selectedDraft.completedAt).toLocaleString()}
              </div>
            )}
            {selectedDraft.documentIds && (
              <div>
                <strong>Document IDs:</strong> {selectedDraft.documentIds}
              </div>
            )}
            <Divider />
            <div>
              <strong>Process Data:</strong>
              <pre className="bg-gray-100 p-4 rounded-md mt-2 overflow-auto max-h-80 dark:bg-gray-700 dark:text-gray-300">
                {JSON.stringify(JSON.parse(selectedDraft.processData), null, 2)}
              </pre>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DraftsManagementView;
